import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import LottieDefault from 'lottie-react';
import { useTaraUI } from '@/contexts/TaraContext';
import taraAnimation from '@/assets/tara-sparkle-burst.json';

// Resolve the component whether the bundler hands us the component directly
// or a module-namespace object ({ default: Lottie }) via CJS/ESM interop.
const Lottie: typeof LottieDefault =
  (LottieDefault as unknown as { default?: typeof LottieDefault }).default ?? LottieDefault;

export function TaraFab(): JSX.Element {
  const { open, setOpen, suggestion, setSuggestion, askTara } = useTaraUI();
  const [hovered, setHovered] = useState(false);

  const showSuggestion = !!suggestion && !open;

  return (
    <>
      <AnimatePresence>
        {showSuggestion && suggestion && (
          <motion.div
            key="tara-suggestion"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 12 }}
            transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
            className="fixed bottom-[4.25rem] right-4 sm:bottom-[5.25rem] sm:right-6 z-40"
          >
            <div className="flex items-center gap-0.5 rounded-full border pl-3 pr-1 py-1 shadow-lg" style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
              <button
                onClick={() => askTara(suggestion.prompt)}
                className="flex items-center gap-1.5 text-xs font-medium transition-colors"
                style={{ color: 'var(--color-primary)' }}
              >
                <Lottie animationData={taraAnimation} loop className="w-4 h-4 shrink-0" />
                {suggestion.label}
              </button>
              <button
                onClick={() => setSuggestion(null)}
                className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-black/5 transition-colors shrink-0"
                style={{ color: 'var(--color-text-muted)' }}
                aria-label="Dismiss suggestion"
              >
                <X size={11} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        style={{ background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-mid) 100%)' }}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 flex items-center px-1 py-1 rounded-full text-white shadow-lg hover:shadow-xl active:scale-[0.97] transition-shadow duration-300"
        aria-label="Ask TARA"
        title="Ask TARA"
      >
        <Lottie animationData={taraAnimation} loop className="w-12 h-12 shrink-0" />
        <motion.span
          initial={false}
          animate={hovered ? { width: 'auto', opacity: 1 } : { width: 0, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 34, opacity: { duration: 0.15, ease: 'easeOut' } }}
          className="overflow-hidden whitespace-nowrap"
        >
          <span className="block text-sm font-medium pr-3 pl-0.5">Ask TARA</span>
        </motion.span>
      </button>
    </>
  );
}
